import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Lista, Pagina } from "../../components/Pagina.js";
import { Botao, Falha, Girando, Selo, Vazio } from "../../components/ui.js";
import { real, dataHora } from "../../lib/formato.js";

/**
 * Os pagamentos, e a conciliação.
 *
 * O botão "conferir" não é conforto: a documentação do Mercado Pago diz que
 * pagamento feito com credencial de teste **não dispara webhook nenhum**, e em
 * produção a notificação também pode se perder. Sem esta tela, um pagamento
 * aprovado sem crédito só se resolveria no banco, à mão.
 */
export function PagamentosPage() {
  const [filtro, setFiltro] = useState<string>("");
  const utils = trpc.useUtils();

  const lista = trpc.pagamentos.listar.useQuery(filtro === "" ? undefined : { status: filtro });
  const conferir = trpc.pagamentos.conferir.useMutation({
    onSuccess: () => void utils.pagamentos.listar.invalidate(),
  });

  const linhas = lista.data ?? [];

  return (
    <Pagina titulo="Pagamentos" descricao="O que entrou, o que falhou e o que ficou pendente.">
      <div className="mb-4 flex flex-wrap gap-2">
        {["", "pendente", "aprovado", "recusado"].map((s) => (
          <button
            key={s || "todos"}
            onClick={() => setFiltro(s)}
            className={`rounded-peca border px-3 py-2 text-[0.875rem] transition-colors ${
              filtro === s
                ? "border-brasa bg-brasa-fraca text-brasa"
                : "border-borda text-texto-suave hover:text-texto"
            }`}
          >
            {s === "" ? "Todos" : s}
          </button>
        ))}
      </div>

      {conferir.isError && <Falha mensagem={conferir.error.message} />}

      {lista.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : lista.isError ? (
        <Falha mensagem={lista.error.message} />
      ) : linhas.length === 0 ? (
        <Vazio titulo="Nenhum pagamento" descricao="Ainda não houve compra por aqui." />
      ) : (
        <Lista
          itens={linhas}
          chave={(p) => p.id}
          colunas={["Quando", "Cliente", "Pacote", "Valor", "Situação", ""]}
          cartao={(p) => (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{p.cliente ?? "—"}</p>
                <p className="text-[0.8125rem] text-texto-suave">
                  {p.pacote ?? "—"} · {real(p.valorCentavos / 100)}
                </p>
                <p className="text-[0.8125rem] text-texto-fraco">{dataHora(p.createdAt)}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <Situacao pagamento={p} />
                <Botao
                  tom="fantasma"
                  formato="nu"
                  carregando={conferir.isPending && conferir.variables?.id === p.id}
                  onClick={() => conferir.mutate({ id: p.id })}
                >
                  Conferir
                </Botao>
              </div>
            </div>
          )}
          linha={(p) => [
            dataHora(p.createdAt),
            p.cliente ?? "—",
            p.pacote ?? "—",
            real(p.valorCentavos / 100),
            <Situacao key="s" pagamento={p} />,
            <Botao
              key="a"
              tom="quieto"
              carregando={conferir.isPending && conferir.variables?.id === p.id}
              onClick={() => conferir.mutate({ id: p.id })}
            >
              Conferir
            </Botao>,
          ]}
        />
      )}
    </Pagina>
  );
}

/** A situação de um pagamento, com o alerta que importa. */
function Situacao({
  pagamento,
}: {
  pagamento: { status: string; metodo: string; creditadoEm: Date | string | null };
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Selo
        tom={
          pagamento.status === "aprovado"
            ? "fixado"
            : pagamento.status === "recusado"
              ? "descartar"
              : "quieto"
        }
      >
        {pagamento.status}
      </Selo>
      <Selo tom="quieto">{pagamento.metodo}</Selo>
      {/* Aprovado sem crédito é o caso que esta tela existe para encontrar — e
          o único que exige ação de alguém. */}
      {pagamento.status === "aprovado" && pagamento.creditadoEm === null && (
        <Selo tom="descartar">sem crédito</Selo>
      )}
    </div>
  );
}
